import { SiteLayout, PageHero, LightSection, CtaStrip } from "@/components/site/SiteLayout";

const PERSONAS = [
  ["Global Market Access", "You own the sequence. We give you a defensible readiness picture per market, the dependencies between them, and the evidence that unlocks each wave."],
  ["HEOR & Evidence Generation", "You own the studies. We show which evidence travels, which needs adaptation, and where one well-designed asset can serve many markets at once."],
  ["Medical Affairs", "You own the science. We align publications, registries and data generation with what payers — not just clinicians — will ask."],
  ["Pricing & Reimbursement", "You own the number. We map pricing anchors, incumbent precedents and reference-pricing spillovers before they set your corridor for you."],
  ["Leadership & Governance", "You own the decision. We give you one transparent readiness verdict, a prioritized investment case, and the reasoning behind every number."],
  ["Consultancies & Partners", "You own the client. We provide the analytical engine behind multi-market engagements — under your confidentiality terms."],
];

const STAGES = [
  ["Pivotal evidence is being designed", "The highest-leverage moment. Endpoint, comparator and PRO choices made now determine what every payer conversation looks like later.", "Ideal time"],
  ["A launch sequence is being planned", "You have evidence and a target list, but the order, dependencies and gaps are debated by opinion rather than analysis.", "Right time"],
  ["Expanding beyond the first markets", "The first submissions taught you the hard way. Before the next wave, make the evidence you already own work harder.", "Right time"],
  ["A dossier or GVD needs to carry more markets", "Your value story exists but was written for one audience. It now needs to survive very different payer archetypes.", "Right time"],
  ["A submission was rejected or restricted", "Recoverable — if the response is evidence-led rather than resubmission-led. We identify exactly what changed the outcome.", "Still time"],
];

export default function WhoWeServe() {
  return (
    <SiteLayout>
      <PageHero overline="Who We Serve" title="Built for the teams who carry global access."
        lede="Pharmaceutical and MedTech organizations taking products into multiple markets — and the consultancies that support them. Different seats at the table, one shared question: are we ready?" />
      <LightSection overline="By function" title="What each team gets out of an engagement.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PERSONAS.map(([role, text]) => (
            <div key={role} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{role}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{text}</p>
            </div>
          ))}
        </div>
      </LightSection>
      <LightSection alt overline="By situation" title="Where you might be right now — and whether it's the right time.">
        <div className="space-y-px bg-slate-200 border border-slate-200">
          {STAGES.map(([title, text, badge]) => (
            <div key={title} className="bg-white p-6 flex flex-col md:flex-row md:items-start gap-4">
              <span className={`shrink-0 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 text-white ${badge === "Ideal time" ? "bg-emerald-700" : badge === "Still time" ? "bg-amber-600" : "bg-slate-900"}`}>{badge}</span>
              <div>
                <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
                <p className="text-sm text-slate-500 leading-relaxed mt-1.5 max-w-3xl">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6 max-w-2xl">If your product launches in new markets within the next three years, its evidence window is open right now.</p>
      </LightSection>
      <CtaStrip text="Not sure which situation is yours? That's usually the sign it's time to talk." />
    </SiteLayout>
  );
}
